import JSZip from "jszip";
import { CanvasShell } from "@/components/canvas-shell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  Upload,
  Loader2,
  Trash2,
  RefreshCw,
  Check,
  Eye,
  ChevronDown,
  ArrowUpDown,
  Filter,
  Layers,
  Palette,
  LayoutGrid,
  CheckSquare,
  Square,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import Masonry from "react-masonry-css";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { Redesign as RedesignType } from "@shared/schema";

type ActiveFilter = { category: string; value: string; label: string };
type SortOption = "newest" | "oldest" | "name_asc" | "name_desc";

const NICHE_OPTIONS = [
  "Ecommerce", "Nutra", "Skincare", "Weightloss", "Fashion", "SaaS",
  "Food & Beverage", "Fitness", "Health", "Beauty", "Tech", "Finance",
  "Education", "Travel", "Real Estate", "Automotive", "Entertainment",
  "Gaming", "Pets", "Home & Garden", "Sports", "Luxury", "B2B", "Agency",
  "Supplements", "CBD", "Dental", "Hair Care", "Anti-Aging", "Wellness", "UGC",
];

const STYLE_OPTIONS = [
  "Minimalist", "Bold", "Colorful", "Dark", "Light", "Gradient",
  "Photo-centric", "Text-heavy", "Product-focused", "Lifestyle",
  "Before-After", "Testimonial", "Comparison", "UGC-style", "Premium",
  "Playful", "Corporate", "Retro", "Modern", "Neon", "Natural", "Clean",
  "Split-layout", "Grid", "Collage", "Flat-design", "3D", "Illustrated",
];

const masonryBreakpoints = {
  default: 3,
  1024: 3,
  768: 2,
  640: 1,
};

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    analyzing: { bg: "bg-blue-50", text: "text-blue-600", label: "Analyzing..." },
    generating: { bg: "bg-amber-50", text: "text-amber-600", label: "Generating..." },
    completed: { bg: "bg-green-50", text: "text-green-600", label: "Completed" },
    failed: { bg: "bg-red-50", text: "text-red-600", label: "Failed" },
    pending: { bg: "bg-gray-50", text: "text-gray-600", label: "Pending" },
  };
  const c = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${c.bg} ${c.text}`}>
      {(status === "analyzing" || status === "generating") && (
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
      )}
      {c.label}
    </span>
  );
}

function RedesignCard({
  item,
  onClick,
  visibility,
  isSelected,
  isSelecting,
  onToggleSelect,
  onDelete,
  isDeleting,
}: {
  item: RedesignType;
  onClick: () => void;
  visibility: { name: boolean; niche: boolean; style: boolean };
  isSelected: boolean;
  isSelecting: boolean;
  onToggleSelect: (id: number) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}) {
  const displayImage = item.redesignedImageUrl || item.originalImageUrl;
  const isProcessing = item.status === "analyzing" || item.status === "generating";
  const showFooter = (visibility.name && item.name) || (visibility.niche && item.niche?.length) || (visibility.style && item.styleTags?.length) || isProcessing;

  return (
    <div
      data-testid={`card-redesign-${item.id}`}
      className={`mb-3 group rounded-lg overflow-hidden cursor-pointer relative bg-white shadow-sm border ${
        isSelected ? "ring-2 ring-indigo-500 border-indigo-300" : "border-gray-100"
      } ${isProcessing ? "ring-1 ring-amber-200" : ""}`}
      onClick={(e) => {
        if (isSelecting) {
          e.stopPropagation();
          onToggleSelect(item.id);
        } else {
          onClick();
        }
      }}
    >
      <div className="relative">
        <img
          src={displayImage}
          alt={item.name}
          className={`w-full h-auto block ${isProcessing ? "opacity-60" : ""}`}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        {(isSelecting || isSelected) && (
          <button
            className="absolute top-2 right-2 z-10"
            onClick={(e) => { e.stopPropagation(); onToggleSelect(item.id); }}
            data-testid={`checkbox-redesign-${item.id}`}
          >
            {isSelected ? (
              <CheckSquare className="h-5 w-5 text-indigo-600 fill-indigo-100" />
            ) : (
              <Square className="h-5 w-5 text-gray-400 bg-white/80 rounded" />
            )}
          </button>
        )}
        {!isSelecting && (
          <button
            className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 hover:bg-red-50 border border-gray-200 hover:border-red-200 text-gray-600 hover:text-red-600 rounded-full p-1.5 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              if (isDeleting) return;
              if (window.confirm(`Delete "${item.name || "this redesign"}"? This cannot be undone.`)) {
                onDelete(item.id);
              }
            }}
            disabled={isDeleting}
            data-testid={`button-delete-redesign-${item.id}`}
            title="Delete this redesign"
          >
            {isDeleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        {isProcessing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2 shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
              <span className="text-xs font-medium text-gray-700">
                {item.status === "analyzing" ? "AI Analyzing..." : "Generating Redesign..."}
              </span>
            </div>
          </div>
        )}
        {item.redesignedImageUrl && item.status === "completed" && (
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500 text-white">
              Redesigned
            </span>
          </div>
        )}
      </div>
      {showFooter && (
        <div className="px-3 py-2 space-y-1">
          <div className="flex items-center justify-between">
            {visibility.name && item.name && (
              <p className="text-xs font-medium text-gray-900 truncate flex-1" data-testid={`text-redesign-name-${item.id}`}>{item.name}</p>
            )}
            <StatusBadge status={item.status} />
          </div>
          {visibility.niche && item.niche && item.niche.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.niche.map((n) => (
                <span key={n} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">{n}</span>
              ))}
            </div>
          )}
          {visibility.style && item.styleTags && item.styleTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.styleTags.map((s) => (
                <span key={s} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Redesign() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRedesign, setSelectedRedesign] = useState<RedesignType | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [visibility, setVisibility] = useState({ name: true, niche: true, style: false });
  const [showOriginal, setShowOriginal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const isSelecting = selectedIds.size > 0;

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const { data: redesigns = [], isLoading } = useQuery<RedesignType[]>({
    queryKey: ["/api/redesigns"],
    refetchInterval: (query) => {
      const items = query.state.data as RedesignType[] | undefined;
      if (items?.some((r) => r.status === "analyzing" || r.status === "generating")) {
        return 3000;
      }
      return false;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name.replace(/\.[^.]+$/, ""));
      const res = await fetch("/api/redesigns", { method: "POST", body: formData });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/redesigns"] });
      toast({ title: "Ad imported for redesign", description: "AI is analyzing and recreating the ad..." });
    },
    onError: (e: Error) => toast({ title: "Import failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/redesigns/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/redesigns"] });
      toast({ title: "Redesign deleted" });
      setSelectedRedesign(null);
    },
  });

  const purgeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/redesigns", { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to purge redesigns");
      return res.json() as Promise<{ deleted: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/redesigns"] });
      setSelectedRedesign(null);
      setSelectedIds(new Set()); // isSelecting is derived from selectedIds.size, so clearing it exits select mode.
      toast({ title: `Purged ${data.deleted} redesign${data.deleted === 1 ? "" : "s"}` });
    },
    onError: (err: any) => {
      toast({ title: "Purge failed", description: err.message, variant: "destructive" });
    },
  });

  const handlePurgeClick = useCallback(() => {
    const count = redesigns?.length ?? 0;
    if (count === 0) {
      toast({ title: "Nothing to purge", description: "There are no redesigns to delete." });
      return;
    }
    const ok = window.confirm(
      `Permanently delete all ${count} redesign${count === 1 ? "" : "s"}? This cannot be undone.`
    );
    if (ok) purgeMutation.mutate();
  }, [redesigns, purgeMutation, toast]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      uploadMutation.mutate(files[i]);
    }
    e.target.value = "";
  }, [uploadMutation]);

  const addFilter = useCallback((category: string, value: string, label: string) => {
    setActiveFilters((prev) => {
      if (prev.some((f) => f.category === category && f.value === value)) return prev;
      return [...prev, { category, value, label }];
    });
  }, []);

  const removeFilter = useCallback((category: string, value: string) => {
    setActiveFilters((prev) => prev.filter((f) => !(f.category === category && f.value === value)));
  }, []);

  const FORMAT_OPTIONS = ["Square", "Portrait", "Landscape", "Story", "Banner"];

  const filteredRedesigns = useMemo(() => {
    let results = redesigns;

    const nicheFilters = activeFilters.filter((f) => f.category === "Niche").map((f) => f.value);
    const formatFilters = activeFilters.filter((f) => f.category === "Format").map((f) => f.value.toLowerCase());
    const styleFilters = activeFilters.filter((f) => f.category === "Style").map((f) => f.value);

    if (nicheFilters.length > 0) {
      results = results.filter((t) => (t.niche || []).some((n) => nicheFilters.includes(n)));
    }
    if (formatFilters.length > 0) {
      results = results.filter((t) => formatFilters.includes((t.format || "").toLowerCase()));
    }
    if (styleFilters.length > 0) {
      results = results.filter((t) => (t.styleTags || []).some((s) => styleFilters.includes(s)));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description || "").toLowerCase().includes(q) ||
          (t.niche || []).some((n) => n.toLowerCase().includes(q)) ||
          (t.styleTags || []).some((s) => s.toLowerCase().includes(q))
      );
    }

    results = [...results].sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "newest":
        default:
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
    });

    return results;
  }, [redesigns, searchQuery, activeFilters, sortBy]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredRedesigns.map((r) => r.id)));
  }, [filteredRedesigns]);

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(
        ids.map((id) => fetch(`/api/redesigns/${id}`, { method: "DELETE" }))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/redesigns"] });
      toast({ title: `${selectedIds.size} redesign(s) deleted` });
      clearSelection();
    },
    onError: () => toast({ title: "Failed to delete some redesigns", variant: "destructive" }),
  });

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} redesign(s)? This cannot be undone.`)) return;
    bulkDeleteMutation.mutate([...selectedIds]);
  }, [selectedIds, bulkDeleteMutation]);

  const [isZipping, setIsZipping] = useState(false);
  const handleBulkDownload = useCallback(async () => {
    const items = redesigns.filter((r) => selectedIds.has(r.id) && r.redesignedImageUrl);
    if (items.length === 0) {
      toast({ title: "No completed redesigns selected", description: "Select redesigns that have finished generating.", variant: "destructive" });
      return;
    }
    setIsZipping(true);
    try {
      const zip = new JSZip();
      const results = await Promise.all(
        items.map(async (item, i) => {
          try {
            const res = await fetch(item.redesignedImageUrl!);
            const blob = await res.blob();
            const ext = item.redesignedImageUrl!.includes(".jpeg") || item.redesignedImageUrl!.includes(".jpg") ? "jpg" : "png";
            const safeName = (item.name || "redesign").replace(/[^a-zA-Z0-9_\-() ]/g, "").trim();
            return { name: `${safeName}_${i + 1}.${ext}`, blob };
          } catch {
            return null;
          }
        })
      );
      for (const r of results) {
        if (r) zip.file(r.name, r.blob);
      }
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `redesigns_${items.length}_images.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: `Downloaded ${items.length} image(s) as ZIP` });
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    } finally {
      setIsZipping(false);
    }
  }, [selectedIds, redesigns, toast]);

  const navigateRedesign = (direction: "prev" | "next") => {
    if (!selectedRedesign) return;
    const currentIndex = filteredRedesigns.findIndex((t) => t.id === selectedRedesign.id);
    if (currentIndex === -1) return;
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < filteredRedesigns.length) {
      setSelectedRedesign(filteredRedesigns[newIndex]);
      setShowOriginal(false);
    }
  };

  const currentIndex = selectedRedesign
    ? filteredRedesigns.findIndex((t) => t.id === selectedRedesign.id)
    : -1;

  useEffect(() => {
    if (selectedRedesign) {
      const updated = redesigns.find((r) => r.id === selectedRedesign.id);
      if (updated && updated.status !== selectedRedesign.status) {
        setSelectedRedesign(updated);
      }
    }
  }, [redesigns, selectedRedesign]);

  return (
    <CanvasShell>
      <div className="flex flex-col h-full bg-gray-50">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        <div className="border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3 px-6 py-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-gray-900">Redesign</h2>
            </div>

            <div className="relative flex-1 ml-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="input-search-redesigns"
                placeholder="Search redesigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 border-gray-200 bg-gray-50 focus:bg-white rounded-full"
              />
              {searchQuery && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setSearchQuery("")}
                  data-testid="button-clear-search"
                >
                  <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            <Button
              data-testid="button-select-redesigns"
              variant={isSelecting ? "default" : "outline"}
              size="sm"
              className={`rounded-full ${isSelecting ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}
              onClick={() => isSelecting ? clearSelection() : selectAll()}
            >
              <CheckSquare className="h-4 w-4 mr-1.5" />
              {isSelecting ? `${selectedIds.size} Selected` : "Select"}
            </Button>

            <Button
              data-testid="button-purge-redesigns"
              onClick={handlePurgeClick}
              disabled={purgeMutation.isPending || (redesigns?.length ?? 0) === 0}
              variant="outline"
              size="sm"
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              title="Delete ALL redesigns"
            >
              {purgeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Purging...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Purge
                </>
              )}
            </Button>

            <Button
              data-testid="button-import-redesign"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
              size="sm"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1.5" />
                  Import Redesign
                </>
              )}
            </Button>
          </div>

          <div className="flex items-center justify-between px-6 py-2 border-t border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    data-testid="button-add-filter"
                  >
                    <Filter className="h-3.5 w-3.5" />
                    Add Filter
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Layers className="h-3.5 w-3.5 mr-2" />
                      Niche
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                      {NICHE_OPTIONS.map((niche) => {
                        const isActive = activeFilters.some((f) => f.category === "Niche" && f.value === niche);
                        return (
                          <DropdownMenuItem
                            key={niche}
                            onClick={() => isActive ? removeFilter("Niche", niche) : addFilter("Niche", niche, niche)}
                          >
                            <span className="flex-1">{niche}</span>
                            {isActive && <Check className="h-3.5 w-3.5 text-indigo-600" />}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <LayoutGrid className="h-3.5 w-3.5 mr-2" />
                      Format
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {FORMAT_OPTIONS.map((format) => {
                        const isActive = activeFilters.some((f) => f.category === "Format" && f.value === format);
                        return (
                          <DropdownMenuItem
                            key={format}
                            onClick={() => isActive ? removeFilter("Format", format) : addFilter("Format", format, format)}
                          >
                            <span className="flex-1">{format}</span>
                            {isActive && <Check className="h-3.5 w-3.5 text-indigo-600" />}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Palette className="h-3.5 w-3.5 mr-2" />
                      Style
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                      {STYLE_OPTIONS.map((style) => {
                        const isActive = activeFilters.some((f) => f.category === "Style" && f.value === style);
                        return (
                          <DropdownMenuItem
                            key={style}
                            onClick={() => isActive ? removeFilter("Style", style) : addFilter("Style", style, style)}
                          >
                            <span className="flex-1">{style}</span>
                            {isActive && <Check className="h-3.5 w-3.5 text-indigo-600" />}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>

              {activeFilters.map((filter) => (
                <span
                  key={`${filter.category}-${filter.value}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                >
                  <span className="text-gray-400 text-[10px]">{filter.category}</span>
                  <span className="mx-0.5 text-gray-300">|</span>
                  {filter.label}
                  <button
                    onClick={() => removeFilter(filter.category, filter.value)}
                    className="ml-0.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors"
                    data-testid="button-sort"
                  >
                    <ArrowUpDown className="h-3.5 w-3.5" />
                    {sortBy === "newest" ? "Newest" : sortBy === "oldest" ? "Oldest" : sortBy === "name_asc" ? "A-Z" : "Z-A"}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {[
                    { value: "newest" as SortOption, label: "Newest" },
                    { value: "oldest" as SortOption, label: "Oldest" },
                    { value: "name_asc" as SortOption, label: "Name A-Z" },
                    { value: "name_desc" as SortOption, label: "Name Z-A" },
                  ].map((opt) => (
                    <DropdownMenuItem key={opt.value} onClick={() => setSortBy(opt.value)}>
                      <span className="flex-1">{opt.label}</span>
                      {sortBy === opt.value && <Check className="h-3.5 w-3.5 text-indigo-600" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu open={visibilityOpen} onOpenChange={setVisibilityOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors"
                    data-testid="button-visibility"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Card Details</div>
                  <DropdownMenuSeparator />
                  {[
                    { key: "name" as const, label: "Name" },
                    { key: "niche" as const, label: "Niche Tags" },
                    { key: "style" as const, label: "Style Tags" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>{item.label}</span>
                      <Switch
                        checked={visibility[item.key]}
                        onCheckedChange={(checked) => setVisibility((prev) => ({ ...prev, [item.key]: checked }))}
                      />
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 py-6">
          <div className="mb-4 text-sm font-semibold text-gray-900" data-testid="text-redesign-count">
            {filteredRedesigns.length} Redesign{filteredRedesigns.length !== 1 ? "s" : ""}
            {activeFilters.length > 0 && (
              <span className="font-normal text-gray-500"> (filtered)</span>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredRedesigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                {redesigns.length === 0 ? <Upload className="h-6 w-6 text-gray-400" /> : <Search className="h-6 w-6 text-gray-400" />}
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                {redesigns.length === 0 ? "No redesigns yet" : "No redesigns found"}
              </h3>
              <p className="text-sm text-gray-500 mt-1 max-w-md">
                {redesigns.length === 0
                  ? "Import competitor ads and AI will recreate them with brand logos removed, new models, and generic products."
                  : "Try different search terms or adjust your filters."}
              </p>
              {redesigns.length === 0 && (
                <Button
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-import-first"
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  Import Redesign
                </Button>
              )}
            </div>
          ) : (
            <Masonry
              breakpointCols={masonryBreakpoints}
              className="flex -ml-4 w-auto"
              columnClassName="pl-4 bg-clip-padding"
            >
              {filteredRedesigns.map((item) => (
                <RedesignCard
                  key={item.id}
                  item={item}
                  onClick={() => { setSelectedRedesign(item); setShowOriginal(false); }}
                  visibility={visibility}
                  isSelected={selectedIds.has(item.id)}
                  isSelecting={isSelecting}
                  onToggleSelect={toggleSelect}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  isDeleting={deleteMutation.isPending && deleteMutation.variables === item.id}
                />
              ))}
            </Masonry>
          )}
        </div>

        {isSelecting && (
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg px-6 py-3 flex items-center justify-between z-20">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-900">
                {selectedIds.size} selected
              </span>
              <button
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={selectAll}
                data-testid="button-select-all"
              >
                Select All ({filteredRedesigns.length})
              </button>
              <button
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                onClick={clearSelection}
                data-testid="button-clear-selection"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDownload}
                disabled={isZipping}
                data-testid="button-bulk-download"
              >
                {isZipping ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1.5" />
                )}
                {isZipping ? "Zipping..." : `Download ZIP (${selectedIds.size})`}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
                data-testid="button-bulk-delete"
              >
                {bulkDeleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1.5" />
                )}
                Delete ({selectedIds.size})
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={!!selectedRedesign}
        onOpenChange={(open) => {
          if (!open) { setSelectedRedesign(null); setShowOriginal(false); }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-white">
          <VisuallyHidden>
            <DialogTitle>Redesign Preview</DialogTitle>
          </VisuallyHidden>
          {selectedRedesign && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {selectedRedesign.name}
                  </span>
                  <StatusBadge status={selectedRedesign.status} />
                  {selectedRedesign.niche?.map((n) => (
                    <span key={n} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600">
                      {n}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {selectedRedesign.redesignedImageUrl && (
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          !showOriginal ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                        onClick={() => setShowOriginal(false)}
                        data-testid="button-show-redesigned"
                      >
                        Redesigned
                      </button>
                      <button
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          showOriginal ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                        onClick={() => setShowOriginal(true)}
                        data-testid="button-show-original"
                      >
                        Original
                      </button>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentIndex <= 0}
                    onClick={() => navigateRedesign("prev")}
                    data-testid="button-prev-redesign"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-gray-500 min-w-[60px] text-center">
                    {currentIndex + 1} of {filteredRedesigns.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentIndex >= filteredRedesigns.length - 1}
                    onClick={() => navigateRedesign("next")}
                    data-testid="button-next-redesign"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100 p-4">
                {(selectedRedesign.status === "analyzing" || selectedRedesign.status === "generating") && !selectedRedesign.redesignedImageUrl ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
                    <p className="text-sm text-gray-600">
                      {selectedRedesign.status === "analyzing"
                        ? "AI is analyzing the ad and planning the redesign..."
                        : "Generating the redesigned ad..."}
                    </p>
                  </div>
                ) : (
                  <img
                    src={showOriginal ? selectedRedesign.originalImageUrl : (selectedRedesign.redesignedImageUrl || selectedRedesign.originalImageUrl)}
                    alt={selectedRedesign.name}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-sm"
                    data-testid={`img-redesign-preview-${selectedRedesign.id}`}
                  />
                )}
              </div>

              <div className="px-4 py-3 border-t border-gray-200">
                {selectedRedesign.description && (
                  <p className="text-sm text-gray-600 mb-2">{selectedRedesign.description}</p>
                )}
                {selectedRedesign.generatedPrompt && (
                  <details className="mb-2">
                    <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">View AI prompt</summary>
                    <p className="text-xs text-gray-500 mt-1 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">{selectedRedesign.generatedPrompt}</p>
                  </details>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {selectedRedesign.styleTags?.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 capitalize">{selectedRedesign.format} format</span>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="button-delete-redesign"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => {
                        if (confirm("Delete this redesign?")) {
                          deleteMutation.mutate(selectedRedesign.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                    {selectedRedesign.redesignedImageUrl && (
                      <a
                        href={selectedRedesign.redesignedImageUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                        data-testid="button-download-redesign"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CanvasShell>
  );
}
